/**
 * EditableRelationshipManager - Scoped relationship change tracking
 *
 * Wraps a RelationshipManager to provide:
 * - Isolated change tracking (_pendingOps scoped to editing session)
 * - Diffing against GraphStore baseline (not current RelationshipManager state)
 * - ConstructionSpec-aware nested object creation
 * - Recursive PATCH generation for nested modifications
 *
 * Lifetime: Tied to EditableWrapper, destroyed on commit/discard
 */

import { RelationshipManager } from './relationship-manager';
import { GraphStore, graphStoreJotaiStore } from './graph-store';
import { getFrameworkResolvers, type ConstructionSpec } from './resolvers';
import { atom, type PrimitiveAtom } from 'jotai';
import { localUuid } from './uuid';
import {
  registerPendingLocalUuid,
  removePendingLocalUuid,
  checkAndApplyBufferedPatchesForAllContexts
} from './local-uuid-registry';
import { findRelationshipProperty } from './reverse-property';

/**
 * An add entry that may carry edge properties
 */
interface AddEntry<TTarget> {
  target: TTarget;
  rel_props?: Record<string, any>;
}

/**
 * An update_rel entry: target + edge properties to update
 */
interface UpdateRelEntry<TTarget> {
  target: TTarget;
  rel_props: Record<string, any>;
}

/**
 * Pending relationship operations tracked during editing session
 */
interface PendingOps<TTarget> {
  add?: AddEntry<TTarget>[];
  remove?: TTarget[];
  set?: TTarget | TTarget[] | null;
  modify?: TTarget[];
  update_rel?: UpdateRelEntry<TTarget>[];
}

/**
 * EditableRelationshipManager provides scoped relationship tracking
 */
export class EditableRelationshipManager<TTarget> {
  private originalManager: RelationshipManager<any, TTarget>;
  private baselineIds: Set<string>;
  private constructionSpec: ConstructionSpec | undefined;
  private graphStoreInstance: GraphStore;
  private fieldName: string;

  // Scoped pending operations (lifetime = editing session)
  private _pendingOps: PendingOps<TTarget> = {};

  // Version atom for reactive updates - incremented on any mutation
  private _versionAtom: PrimitiveAtom<number> = atom(0);

  // Cache of nested editables by node ID — stable references across iterations
  private _nestedEditables: Map<string, any> = new Map();

  constructor(
    originalManager: RelationshipManager<any, TTarget>,
    baselineIds: string[],
    constructionSpec: ConstructionSpec | undefined,
    graphStoreInstance: GraphStore
  ) {
    this.originalManager = originalManager;
    this.fieldName = originalManager.propertyName;
    this.baselineIds = new Set(baselineIds);
    this.constructionSpec = constructionSpec;
    this.graphStoreInstance = graphStoreInstance;
  }

  /**
   * Get or create an editable wrapper for an existing relationship target.
   * The wrapper inherits this manager's nested construction spec, so nested
   * relationships allowed by the parent spec (e.g. answer on Question via
   * Questionnaire's spec) work correctly.
   *
   * Lazy: only registers as a modify op when the wrapper is actually mutated.
   * Cached: returns the same wrapper for the same node ID across calls.
   */
  getEditable(node: TTarget): any {
    const nodeId = (node as any).id;
    if (!nodeId) throw new Error('getEditable requires a node with an ID');

    if (this._nestedEditables.has(nodeId)) {
      return this._nestedEditables.get(nodeId)!;
    }

    // Lazy import to avoid circular dependency (editable-wrapper imports this file)
    const { EditableWrapper } = require('./editable-wrapper');
    const wrapper = new EditableWrapper(node, this.graphStoreInstance, this.constructionSpec);

    this._nestedEditables.set(nodeId, wrapper);

    // Register as modify op — serializeForPatch detects EditableWrapper via buildPatch()
    // and only includes it if buildPatch() returns non-empty (unchanged wrappers are dropped)
    if (!this._pendingOps.modify) this._pendingOps.modify = [];
    this._pendingOps.modify.push(wrapper);
    return wrapper;
  }

  /**
   * Iterate current relationship targets as nested editables.
   * Each yielded item inherits this manager's nested construction spec.
   * Lazy: wrappers are only registered as modify ops when actually mutated.
   */
  *[Symbol.iterator](): Iterator<any> {
    const sourceId = (this.originalManager as any)._sourceInstance?.id;
    if (!sourceId) return;
    const targetNodes = this.graphStoreInstance.getTargetNodes(sourceId, this.fieldName);
    for (const node of targetNodes) {
      yield this.getEditable(node as TTarget);
    }
  }

  /**
   * Notify subscribers that pending ops have changed
   * Triggers re-render in useEditableNodes hook
   */
  private notifyChange(): void {
    const current = graphStoreJotaiStore.get(this._versionAtom);
    graphStoreJotaiStore.set(this._versionAtom, current + 1);
  }

  /**
   * Add target nodes to this relationship
   * Tracks operation for later PATCH generation
   *
   * Supports three calling styles:
   * - add(target, { role: 'lead' })     — single target with edge properties
   * - add(target1, target2)              — bulk add without edge properties
   * - add({ node: t1, rel_props: {...} }, { node: t2, rel_props: {...} }) — bulk with per-target edge props
   */
  add(target: TTarget, relProps: Record<string, any>): void;
  add(...targets: Array<TTarget | { node: TTarget; rel_props: Record<string, any> }>): void;
  add(...args: any[]): void {
    this.validateModifyCapability();
    if (!this._pendingOps.add) {
      this._pendingOps.add = [];
    }

    // Detect: add(target, relPropsObject) — second arg is a plain object with no 'node' key
    if (args.length === 2 && typeof args[1] === 'object' && args[1] !== null && !('node' in args[1]) && !('id' in args[1])) {
      this._pendingOps.add.push({ target: args[0], rel_props: args[1] });
      this.notifyChange();
      return;
    }

    // Otherwise process each arg
    for (const arg of args) {
      if (arg && typeof arg === 'object' && 'node' in arg && 'rel_props' in arg) {
        // Wrapper object format: { node: target, rel_props: {...} }
        this._pendingOps.add.push({ target: arg.node, rel_props: arg.rel_props });
      } else {
        // Plain target
        this._pendingOps.add.push({ target: arg });
      }
    }
    this.notifyChange();
  }

  /**
   * Remove target nodes from this relationship
   * Tracks operation for later PATCH generation
   */
  remove(...targets: TTarget[]): void {
    this.validateModifyCapability();
    if (!this._pendingOps.remove) {
      this._pendingOps.remove = [];
    }
    this._pendingOps.remove.push(...targets);
    this.notifyChange();
  }

  /**
   * Set relationship to specific target(s), replacing all existing
   * Tracks operation for later PATCH generation
   */
  set(target: TTarget | TTarget[] | null): void {
    this.validateModifyCapability();
    this._pendingOps.set = target;
    this.notifyChange();
  }

  /**
   * Modify existing target nodes (for nested modifications)
   * Tracks operation for later PATCH generation
   */
  modify(...targets: TTarget[]): void {
    this.validateModifyCapability();
    if (!this._pendingOps.modify) {
      this._pendingOps.modify = [];
    }
    this._pendingOps.modify.push(...targets);
    this.notifyChange();
  }

  /**
   * Update edge properties on an existing relationship
   * Tracks operation for later PATCH generation
   */
  updateRel(target: TTarget, relProps: Record<string, any>): void {
    this.validateModifyCapability();
    if (!this._pendingOps.update_rel) {
      this._pendingOps.update_rel = [];
    }
    this._pendingOps.update_rel.push({ target, rel_props: relProps });
    this.notifyChange();
  }

  /**
   * Validate that this relationship can be modified according to ConstructionSpec
   * @throws Error if relationship is not in MODIFY_CONSTRUCTION_SPEC
   */
  private validateModifyCapability(): void {
    // Get source model name from the source instance
    const sourceModelName = (this.originalManager as any)._sourceInstance?._label;

    if (!sourceModelName) {
      // If no model name available, allow modification (backward compatibility)
      return;
    }

    // Get MODIFY_CONSTRUCTION_SPEC for this model
    const { getModifyConstructionSpec, isRelationshipAllowed } = getFrameworkResolvers();
    const spec = getModifyConstructionSpec(sourceModelName);

    if (!spec) {
      // No spec defined for this model - allow all modifications
      return;
    }

    // Check if this relationship field is allowed in the spec
    if (!isRelationshipAllowed(spec, this.fieldName)) {
      // Get list of allowed relationships for helpful error message
      const allowedFields = Object.keys(spec).filter(k => k !== 'max_count');

      throw new Error(
        `Cannot modify relationship '${this.fieldName}' on ${sourceModelName}. ` +
        `This relationship is not included in the MODIFY_CONSTRUCTION_SPEC. ` +
        `Only relationships defined in the ConstructionSpec can be modified. ` +
        (allowedFields.length > 0
          ? `Available relationships: ${allowedFields.join(', ')}`
          : 'No relationships are modifiable on this model.')
      );
    }
  }

  /**
   * Build PATCH by diffing pending operations against GraphStore baseline
   * Filters out redundant operations and recursively handles nested modifications
   */
  buildPatch(specOverride?: ConstructionSpec): any | null {
    const patch: any = {};
    // the spec of the PATH this manager is serialised under: a nested
    // editable (the thread a task's editable modifies) is judged by its
    // parent's slice, as the server judges the parent's PATCH
    const spec = specOverride ?? this.constructionSpec;

    // 1. Process 'add' operations - filter out items already in baseline
    if (this._pendingOps.add && this._pendingOps.add.length > 0) {
      const addItems = this._pendingOps.add
        .filter(entry => {
          // If item has ID and it's already in baseline, skip it
          const itemId = (entry.target as any).id;
          return !itemId || !this.baselineIds.has(itemId);
        })
        .map(entry => {
          const serialized = this.serializeForPatch(entry.target, 0, spec);
          if (entry.rel_props) {
            serialized._rel_props = entry.rel_props;
          }
          return serialized;
        });

      if (addItems.length > 0) {
        patch.add = addItems;
      }
    }

    // 2. Process 'remove' operations - filter out items NOT in baseline
    if (this._pendingOps.remove && this._pendingOps.remove.length > 0) {
      const removeItems = this._pendingOps.remove
        .filter(item => {
          // Only remove items that ARE in baseline
          const itemId = (item as any).id;
          return itemId && this.baselineIds.has(itemId);
        })
        .map(item => ({ id: (item as any).id }));

      if (removeItems.length > 0) {
        patch.remove = removeItems;
      }
    }

    // 3. Process 'set' operations - always wrap in array (backend requires list)
    if (this._pendingOps.set !== undefined) {
      const setValue = this._pendingOps.set;

      if (setValue === null) {
        // Clear all relationships
        patch.set = [];
      } else if (Array.isArray(setValue)) {
        // Set to array of items
        patch.set = setValue.map(item => this.serializeForPatch(item, 0, spec));
      } else {
        // Set to single item - wrap in array
        patch.set = [this.serializeForPatch(setValue, 0, spec)];
      }
    }

    // 4. Process 'modify' operations - recursively build patches for existing items
    if (this._pendingOps.modify && this._pendingOps.modify.length > 0) {
      const modifyItems = this._pendingOps.modify
        .filter(item => {
          // Only modify items that ARE in baseline
          const itemId = (item as any).id;
          return itemId && this.baselineIds.has(itemId);
        })
        .map(item => this.serializeForPatch(item, 0, spec))
        .filter(item => {
          // Drop unchanged items (e.g. nested editables with no mutations)
          if (!item || typeof item !== 'object') return false;
          return Object.keys(item).some(k => k !== 'id');
        });

      if (modifyItems.length > 0) {
        patch.modify = modifyItems;
      }
    }

    // 5. Process 'update_rel' operations - update edge properties on existing relationships
    if (this._pendingOps.update_rel && this._pendingOps.update_rel.length > 0) {
      const updateRelItems = this._pendingOps.update_rel
        .filter(entry => {
          const itemId = (entry.target as any).id;
          return itemId && this.baselineIds.has(itemId);
        })
        .map(entry => ({
          id: (entry.target as any).id,
          _rel_props: entry.rel_props,
        }));

      if (updateRelItems.length > 0) {
        patch.update_rel = updateRelItems;
      }
    }

    // Return null if no actual operations after filtering
    return Object.keys(patch).length > 0 ? patch : null;
  }

  /**
   * Recursively serialize an item for PATCH operations
   * Handles nested relationship modifications according to ConstructionSpec
   *
   * For new items (no ID), generates a LocalUUID and stores it on the item
   * so we can map it to the server UUID when the response arrives.
   */
  private serializeForPatch(item: any, depth: number, spec: ConstructionSpec | undefined = this.constructionSpec): any {
    if (!item || typeof item !== 'object') {
      return item;
    }

    // Handle arrays
    if (Array.isArray(item)) {
      return item.map(i => this.serializeForPatch(i, depth, spec));
    }

    const result: any = {};

    // Check if item is an EditableWrapper FIRST - before any early returns
    // EditableWrapper needs to use buildPatch() to include nested pending ops
    if (typeof item.buildPatch === 'function') {
      // This is an EditableWrapper - it has its own baseline and can diff
      // under THIS path's spec (the parent's slice), not the wrapper's own
      const patch = item.buildPatch(spec);
      // Must include ID for modify operations
      if (item.id) {
        return { id: item.id, ...patch };
      }
      return patch;
    }

    // If item has an existing (non-local) ID, just send the ID reference
    // This is a reference to an existing object - no need to send all properties
    if (item.id && typeof item.id === 'string' && !item.id.startsWith('local-')) {
      return { id: item.id };
    }

    // If item has an ID only (reference to existing object), just include ID
    if (item.id && Object.keys(item).length === 1) {
      return { id: item.id };
    }

    // A NEW node: no server id yet (none, or a local one)
    const isNew = !item.id || (typeof item.id === 'string' && item.id.startsWith('local-'));

    // For new items without an ID, generate a LocalUUID
    // Set it as the item's ID so it displays correctly and can be tracked
    if (!item.id) {
      const localId = localUuid();
      item.id = localId;           // Set as item ID for display/tracking
      item._localUuid = localId;   // Keep reference for uuid_map matching
      result.id = localId;         // Include in PATCH - server will map to real UUID
      result._localUuid = localId; // Include for server to return in uuid_map

      // Register as pending - DAG patches with this localUuid will be buffered
      // until modify response arrives
      registerPendingLocalUuid(localId);
    }

    // Serialize scalar properties
    // NOTE: For non-EditableWrapper objects, we serialize ALL properties
    // This means for modify operations, the caller should ideally use EditableWrapper
    // Otherwise we can't tell which scalar properties actually changed
    for (const key in item) {
      if (!item.hasOwnProperty(key)) continue;
      if (key.startsWith('_')) continue;
      if (typeof item[key] === 'function') continue;

      const value = item[key];

      // Skip if this looks like a RelationshipManager
      if (value && typeof value === 'object' && 'add' in value && 'remove' in value) {
        continue;
      }

      result[key] = value;
    }

    // CRITICAL: Include _label for type discrimination (e.g., PDMAction vs ActionDefinition)
    // _label is a getter property so it won't appear in for...in loop
    if (item._label) {
      result._label = item._label;
    }

    // Handle nested relationships if item is a NeoNodeBase instance
    if (typeof item.getRelationshipProperties === 'function') {
      const relationshipProps = item.getRelationshipProperties();

      const { isRelationshipAllowed } = getFrameworkResolvers();
      for (const propName of relationshipProps) {
        // Check if this nested relationship is allowed by ConstructionSpec:
        // this editable's OWN spec, never the parent path's: an op outside
        // the path the server judges goes out and is refused there, visibly
        // (Tom: never fail silently), not dropped here
        if (!isRelationshipAllowed(this.constructionSpec, propName)) {
          continue;
        }

        const manager = item[propName];

        // If manager has an EditableRelationshipManager, get its patch
        if (manager && typeof manager.buildPatch === 'function') {
          const nestedPatch = manager.buildPatch();
          if (nestedPatch && Object.keys(nestedPatch).length > 0) {
            result[propName] = nestedPatch;
          }
        }
        // Otherwise check if it has pending ops (fallback for direct manager access)
        else if (manager && typeof manager.getPendingOps === 'function') {
          const pendingOps = manager.getPendingOps();
          if (pendingOps && Object.keys(pendingOps).length > 0) {
            // Recursively serialize nested operations
            const nestedPatch: any = {};

            if (pendingOps.add) {
              nestedPatch.add = (Array.isArray(pendingOps.add) ? pendingOps.add : [pendingOps.add])
                .map((nestedItem: any) => this.serializeForPatch(nestedItem, depth + 1));
            }

            if (pendingOps.remove) {
              nestedPatch.remove = (Array.isArray(pendingOps.remove) ? pendingOps.remove : [pendingOps.remove])
                .map((nestedItem: any) => ({ id: (nestedItem as any).id }));
            }

            if (pendingOps.set !== undefined) {
              if (pendingOps.set === null) {
                nestedPatch.set = null;
              } else if (Array.isArray(pendingOps.set)) {
                nestedPatch.set = pendingOps.set.map((nestedItem: any) => this.serializeForPatch(nestedItem, depth + 1));
              } else {
                nestedPatch.set = this.serializeForPatch(pendingOps.set, depth + 1);
              }
            }

            if (Object.keys(nestedPatch).length > 0) {
              result[propName] = nestedPatch;
            }
          }
        }
      }
    }

    // A new node's relationships given to its CONSTRUCTOR
    // (`new Step({ text, context_items: ids })`, the chat composer's turn):
    // NeoNodeBase keeps them aside in `_constructorRelationships` for its own
    // create (neo-node-base.ts); added here, they go with it the same way, as
    // ids or id objects. Only where the spec of the PATH this is serialised
    // under allows them (the task's thread.steps for a turn added through
    // the thread's own editable: the spec the server judges the PATCH by),
    // and never over what a manager's own ops already sent.
    const fromConstructor = isNew ? item._constructorRelationships : undefined;
    if (fromConstructor && typeof fromConstructor === 'object') {
      const { isRelationshipAllowed, getNestedSpec } = getFrameworkResolvers();
      for (const [propName, value] of Object.entries(fromConstructor)) {
        if (propName in result || value === undefined || value === null) continue;
        if (!isRelationshipAllowed(spec, propName)) continue;
        result[propName] = this.serializeForPatch(value, depth + 1, getNestedSpec(spec, propName));
      }
    }

    return result;
  }

  /**
   * Apply pending operations to GraphStore
   * Called when EditableWrapper commits
   *
   * Note: We don't apply to originalManager because it's read-only in the frontend.
   * The GraphStore is updated by the parent EditableWrapper.updateNode() call.
   */
  applyToOriginal(): void {
    // Note: In the frontend, RelationshipManager is read-only (query-only).
    // The actual graph updates happen via the PATCH API and are reflected
    // in GraphStore when the server confirms the modification.
    //
    // This method is a no-op in the frontend - the parent EditableWrapper
    // handles updating GraphStore with the server response.
  }

  /**
   * Get pending operations (for debugging)
   */
  getPendingOps(): PendingOps<TTarget> {
    return { ...this._pendingOps };
  }

  /**
   * Check if there are any pending operations
   */
  hasPendingOps(): boolean {
    return Object.keys(this._pendingOps).length > 0;
  }

  /**
   * Promote pending add operations to GraphStore.
   * Called when modify API confirms success.
   *
   * Clean Architecture:
   * 1. Remove localUuids from pending (so DAG buffering system knows they're resolved)
   * 2. Check if any buffered DAG patches can now be applied
   * 3. Buffered patches contain server-calculated values AND relationships
   *
   * Note: We don't manually add items to GraphStore here. The buffered DAG patches
   * will be applied automatically when all pending items are resolved, bringing
   * server-calculated values AND relationships together.
   *
   * If DAG hasn't arrived yet (no buffer), the DAG patch will arrive later and
   * apply normally since the localUuid is no longer pending.
   *
   * @param uuidMap - Maps _localUuid to server-assigned UUID (from modify response)
   */
  promoteToGraphStore(uuidMap: Record<string, string> = {}): void {
    // Remove local UUIDs from pending for all operation types that create new nodes

    // Handle 'add' operations
    const pendingAdds = this._pendingOps.add || [];
    for (const entry of pendingAdds) {
      const localUuidValue = (entry.target as any)._localUuid;
      if (localUuidValue && uuidMap[localUuidValue]) {
        removePendingLocalUuid(localUuidValue);
      }
    }

    // Handle 'set' operations (can contain new nodes with local UUIDs)
    if (this._pendingOps.set !== undefined && this._pendingOps.set !== null) {
      const setItems = Array.isArray(this._pendingOps.set)
        ? this._pendingOps.set
        : [this._pendingOps.set];
      for (const item of setItems) {
        const localUuidValue = (item as any)?._localUuid;
        if (localUuidValue && uuidMap[localUuidValue]) {
          removePendingLocalUuid(localUuidValue);
        }
      }
    }

    // Check if any buffered patches can now be applied
    checkAndApplyBufferedPatchesForAllContexts();
  }

  /**
   * Apply this session's CONFIRMED relationship ops (set/remove/add) directly
   * to the GraphStore, so GraphStore-backed reads (useNode/useNodes
   * relationship atoms) update without a page reload.
   *
   * Models under a DAG context get the same changes again via patches —
   * that double application is safe: removeRelationship of a missing entry
   * is a no-op and addRelationship refuses to downgrade DAG-sourced
   * entries. Models OUTSIDE any DAG context (e.g. User↔Organisation
   * membership) have no patch lane at all, so this is their only reactive
   * update (2026-08-29: profile page showed a stale organisation after
   * Leave until reload).
   *
   * Must be called ONLY after the server confirms the save —
   * EditableWrapper.save() does; clearPendingOps deliberately does NOT,
   * because it also runs on error/discard paths (e.g. ChatView clears a
   * failed optimistic step).
   */
  applyOpsToGraphStore(uuidMap: Record<string, string> = {}): void {
    const sourceId = (this.originalManager as any)['_sourceInstance']?.id;
    if (!sourceId) return;

    // DAG-owned relationship lists are updated by their patches — don't
    // fight the lane (addRelationship would warn-and-skip per call).
    if (this.graphStoreInstance.isDagSourced(sourceId, this.fieldName)) return;

    const resolveId = (t: any): string | null => {
      const local = t?._localUuid;
      if (local && uuidMap[local]) return uuidMap[local];
      return t?.id ?? null;
    };
    // Ensure the target node itself exists in the store — the reactive
    // read maps relationship target ids through node atoms and drops
    // ids with no node.
    const ensureNode = (t: any): void => {
      if (t && typeof t === 'object' && t.id) {
        this.graphStoreInstance.updateNode(t);
      }
    };

    // Mirror every change onto the INVERSE property of the target class
    // (the same physical edge indexed from the other end) — matching how
    // the DAG client writes both directions. Without this, e.g. an
    // Organisation.users removal leaves User.organisation stale.
    const srcCls = (this.originalManager as any)['_sourceInstance']?.constructor as any;
    const relInfo = srcCls?.relationships?.[this.fieldName] as
      | { relClass: string; direction: 'in' | 'out' }
      | undefined;
    const inverseDir = relInfo?.direction === 'out' ? 'in' : 'out';
    const inversePropFor = (t: any): string | null => {
      if (!relInfo) return null;
      return findRelationshipProperty(t?.constructor as any, relInfo.relClass, inverseDir);
    };
    const mirrorRemove = (t: any, tid: string): void => {
      const inv = inversePropFor(t);
      if (inv) this.graphStoreInstance.removeRelationship(tid, sourceId, inv);
    };
    const mirrorAdd = (t: any, tid: string): void => {
      const inv = inversePropFor(t);
      if (inv && !this.graphStoreInstance.isDagSourced(tid, inv)) {
        this.graphStoreInstance.addRelationship(tid, sourceId, inv);
      }
    };

    if (this._pendingOps.set !== undefined) {
      // Replace semantics: drop every current entry, then add the new set.
      const current = this.graphStoreInstance.getTargetNodes(sourceId, this.fieldName) as any[];
      for (const existing of current) {
        if (existing?.id) {
          this.graphStoreInstance.removeRelationship(sourceId, existing.id, this.fieldName);
          mirrorRemove(existing, existing.id);
        }
      }
      const setValue = this._pendingOps.set;
      const items = setValue == null ? [] : Array.isArray(setValue) ? setValue : [setValue];
      for (const item of items) {
        const id = resolveId(item);
        if (id) {
          ensureNode(item);
          this.graphStoreInstance.addRelationship(sourceId, id, this.fieldName);
          mirrorAdd(item, id);
        }
      }
    }

    for (const t of this._pendingOps.remove ?? []) {
      const id = resolveId(t);
      if (id) {
        this.graphStoreInstance.removeRelationship(sourceId, id, this.fieldName);
        mirrorRemove(t, id);
      }
    }

    for (const entry of this._pendingOps.add ?? []) {
      const id = resolveId(entry.target);
      if (id) {
        ensureNode(entry.target);
        this.graphStoreInstance.addRelationship(
          sourceId,
          id,
          this.fieldName,
          undefined,
          entry.rel_props ? { properties: entry.rel_props } : undefined,
        );
        mirrorAdd(entry.target, id);
      }
    }
  }

  /**
   * Clear all pending operations after promoting to GraphStore.
   * Called after successful save to remove optimistic items that are now in GraphStore.
   *
   * @param uuidMap - Maps _localUuid to server-assigned UUID (from modify response)
   */
  clearPendingOps(uuidMap: Record<string, string> = {}): void {
    // Promote pending adds to GraphStore before clearing
    this.promoteToGraphStore(uuidMap);

    this._pendingOps = {};

    // Propagate clearPendingOps to nested editables so their local UUIDs
    // are resolved and buffered DAG patches can be applied
    for (const nestedEditable of this._nestedEditables.values()) {
      if (typeof nestedEditable.clearAllPendingOps === 'function') {
        nestedEditable.clearAllPendingOps(uuidMap);
      }
    }
    this._nestedEditables.clear();

    this.notifyChange();
  }

  /**
   * Get the version atom for reactive subscriptions
   * Used by useEditableNodes hook
   */
  getVersionAtom(): PrimitiveAtom<number> {
    return this._versionAtom;
  }

  /**
   * Get the underlying RelationshipManager for GraphStore queries
   * Used by useEditableNodes hook
   */
  getOriginalManager(): RelationshipManager<any, TTarget> {
    return this.originalManager;
  }
}
